import { immerable } from "immer";
import { MovieCardProps } from "../../../Components/Cards/MovieCard/movie-card";
import { DbConfidentialityInfos } from "./db-confidentiality-infos";
import { AssetMediaCardDto } from "./db-file-infos";
import { DbLabProviderInfos } from "./db-lab-provider-infos";
import { DbMongoSerializationBaseInfos } from "./db-mongo-serialization-base-infos";
import { DbObjectCreateOrUpdateInfos } from "./db-object-create-or-update-infos";
import { DbObjectProviderHistoryInfos, ProviderHistoryModalDto } from "./db-object-provider-history-infos";
import { DbObjectStatusHistoryInfos, StatusHistoryModalDto } from "./db-object-status-history-infos";
import { DbWorkflowUserTasksInfos } from "./db-workflow-user-tasks-infos";
import { HierarchyTree } from "./hierarchy-tree";
import { IDbBaseMetadataValueInfos } from "./i-db-base-metadata-value-infos";
import { ObjectClass } from "./object-class";
import { ObjectLayer } from "./object-layer";
import { ObjectStatus } from "./object-status";
import { ObjectType } from "./object-type";
import { PermissionInfos } from "./permission-infos";
import { BaseMetadataType } from "./base-metadata-type";
import { PermissionType } from "./permission-type";
import { ObjectTag } from "./object-tag";
import { AssetLinkedResource } from "./asset-resources";

export class DbAssetObjectInfos extends DbMongoSerializationBaseInfos {
  [immerable] = true;
  $type?: any | null | undefined;
  isDeleted: boolean = false;
  hidden: boolean = false;
  trash: boolean = false;
  platformId: number = 0;
  type: ObjectType = ObjectType.Archive; // Default is first of enum => always returned by backend
  objectType?: ObjectType = ObjectType.Archive;
  layer: ObjectLayer = ObjectLayer.Asset; // Default is first of enum => always returned by backend
  status: ObjectStatus = ObjectStatus.New;
  titleCustomerId: string | null = null;
  name: string = "";
  comment: string | null = null;
  parentObjectId: number | null = 0;
  parentTitleId: number | null = null;
  placeholderRowId: number | null = 0;
  providerId: number | null = 0;
  classification: ObjectClass | null = null;
  creationInfos: DbObjectCreateOrUpdateInfos | null = null;
  deleteInfos: DbObjectCreateOrUpdateInfos | null = null;
  uploaderInfos: DbObjectCreateOrUpdateInfos | null = null;
  confidentialityInfos: DbConfidentialityInfos | null = null;
  allParentObjectIds: number[] | null = [];
  relatedObjectIds: number[] | null = null;
  updateInfos: DbObjectCreateOrUpdateInfos[] | null = null;
  providerHistory: DbObjectProviderHistoryInfos[] | null = [];
  followersInfos: DbObjectCreateOrUpdateInfos[] | null = null;
  statusHistory: DbObjectStatusHistoryInfos[] | null = [];
  metadata: IDbBaseMetadataValueInfos[] | null = [];
  parentTitleName: string | null = null;
  childObjects: DbAssetObjectInfos[] | null = null;
  childCount: number | null = null;
  providerInfos: DbLabProviderInfos | null = null;
  relatedObjects: DbAssetObjectInfos[] | null = null;
  parentTitle: DbAssetObjectInfos | null = null;
  parentObject: DbAssetObjectInfos | null = null;
  uploadedByUserName: string | null = null;
  followedByUserNames: string | null = null;
  providerName: string | null = null;
  lastAcceptationDate: Date | null = null;
  lastIngestStartDate: Date | null = null;
  commentAlt: string | null = null;
  nameAlt: string | null = null;
  viewIds: number[] | null = null;
  permsInherit: Record<PermissionType, boolean> | null = null;
  tags: ObjectTag[] | null = null;
  linkedResources: AssetLinkedResource[] | null = null;

  constructor(init?: Partial<DbAssetObjectInfos>) {
    super();
    Object.assign(this, init);
  }
}

// #region DataTree
export interface DataNodeType {
  type: string | "Title" | "Group" | "Asset";
  nodeId: string;
  poster?: string | null;
  name: string;
  desc?: string;
  objectType?: ObjectType;
  children?: DataNodeType[];
  treeChildrenNodeIds?: string[] | undefined | null;
}

export interface TreeGenBuilderItemNodeType {
  nodeId: string;
  children?: TreeGenBuilderItemNodeType[];
  treeChildrenNodeIds: string[];
  name: string;
  desc?: string;
  type: string;
  poster?: string | null;
  objectType?: ObjectType;
}
// #endregion

// #region TitleContent
export interface TitleContentContentAudio {
  lang: string;
  mixes: string[];
  accesses: string[];
}

export interface TitleContentContentAudioByEpisode {
  episodeNb: number;
  audios: TitleContentContentAudio[];
}

export interface TitleContentContentSubtitle {
  lang: string;
  types: string[];
}

export interface TitleContentContentSubtitleByEpisode {
  episodeNb: number;
  subtitles: TitleContentContentSubtitle[];
}

export interface TitleContentContent {
  id: number;
  title: string;
  qualities: string[];
  audio: TitleContentContentAudioByEpisode[];
  subtitles: TitleContentContentSubtitleByEpisode[];
  path: string[];
  duration: string;
  episodeNb: number[];
  allLang: string[];
}

export interface TitleContentData {
  id: number;
  name: string;
  hasConfidentialityInfos: boolean;
  comment: string;
  isSerieTitle: boolean;
  casting: { [key: string]: string[] };
  content: TitleContentContent[];
  allLang: string[];
  tags: ObjectTag[];
  relatedTitles?: Omit<MovieCardProps, "index">[];
  metadataList: TitleContentMetadataList;
  metadataSections: AssetMetadatas | null;
}

export interface TitleContentMetadataList {
  titleType: any;
  serieSeasonEpisodeTotalNumber: any;
  allVideoQualities: any;
  duration: any;
  programRestored: any;
  allAudioLanguages: any;
  allAudioVersions: any;
  allSubtitleLanguages: any;
  imdbId: any;
  titleOriginalPictureFormat: any;
  productionDate: any;
  titleOriginalLanguage: any;
  titleSecondaryOriginalLanguages: any;
  nationalities: any;
  titleGenres: any;
  note: any;
  titles: any;
  synopses: any;
}

// #endregion

// #region AdminAssetSingle - Main
export interface AdminAssetSingleRequestDetails {
  requestedBy: string;
  requestedAt: string;
  assignedProviderId: number | null;
  assignedProviderName: string;
  userCanRemindProvider: boolean;
  userCanAddNewAssignment: boolean;
  userCanDeleteRequest: boolean;
}

export interface AdminAssetSingleDeleteDetails {
  deletedBy: string;
  deletedAt: string;
  deletedComment: string;
  assignedProviderName: string;
}

export interface AdminAssetSingleBypassDetails {
  bypassedBy: string;
  bypassedAt: string;
  bypassedComment: string;
  assignedProviderName: string;
}

export interface AdminAssetSingleRejectionDetails {
  rejectedBy: string;
  rejectedAt: string;
  rejectedComment: string;
  assignedProviderId: number | null;
  assignedProviderName: string;
  userCanPurge: boolean;
  userCanEditMetadata: boolean;
  userCanAddNewAssignment: boolean;
  userCanDeleteRequest: boolean;
  userCanBypassReject: boolean;
  currentStatus: ObjectStatus;
}

export interface AdminAssetSingleTopBarData {
  statusHistory: StatusHistoryModalDto[];
  providerHistory: ProviderHistoryModalDto[];
  userCanAddToBasket: boolean;
  userCanCreateShare: boolean;
  userCanExportXml: boolean;
  userCanExportPdf: boolean;
  userCanEditMetadata: boolean;
  dcpDkdmObjectId: number | null;
}

export interface AdminAssetSinglePendingUploadDetails {
  createdBy: string;
  startDate: string;
  currentTaskName: string;
  isClaimedByUser: boolean;
  userHasRightToClaim: boolean;
  userHasRightToComplete: boolean;
  task: DbWorkflowUserTasksInfos;
  workflowInstanceId: number;
}

export interface AdminAssetSingleDto {
  id: number;
  status: ObjectStatus;
  statusHistory: DbObjectStatusHistoryInfos[];
  type: ObjectType;
  layer: ObjectLayer;
  class: ObjectClass | null;
  name: string;
  topbarData: AdminAssetSingleTopBarData;
  parentTitleId: number | null;
  parentTitleName: string | null;
  parentTitleLayer: ObjectLayer;
  // tells us if we have to display infos about status, type, rejection and request
  // example : Group or Folder doesn't need that infos (never uploaded)
  isAssetUploadable: boolean;
  rejectionDetails: AdminAssetSingleRejectionDetails;
  requestDetails: AdminAssetSingleRequestDetails;
  bypassDetails: AdminAssetSingleBypassDetails;
  deleteDetails: AdminAssetSingleDeleteDetails;
  dcpDkdmObjectId: number | null;
  isBypassed: boolean;
  isTrailer: boolean;
  metadata?: IDbBaseMetadataValueInfos[];
}

// #endregion

// #region AdminAssetSingle - Tabs
// Metadata Tab
export interface AdminAssetSingleMetadataDto {
  parentTitleId: number | null;
  parentTitleName: string;
  parentTitleLayer: ObjectLayer;
  parentObjectId: number | null;
  parentObjectName: string;
  name: string;
  type: ObjectType;
  class: ObjectClass | null;
  metadatas: AssetMetadatas;
}

// Hierarchy
export interface AdminAssetSingleHierarchyDto {
  graph: DbAssetObjectInfos;
  hierarchyTree: HierarchyTree[];
}

// Actions Tab
export interface AdminAssetSingleActionsDto {
  id: number;
  name: string;
  classification: ObjectClass;
  status: ObjectStatus;
  type: ObjectType;
  layer: ObjectLayer;
  currentFollowersIds: number[];
  userHasAclSuperUser: boolean;
  userHasRightToSwitchClass: boolean;
  userHasRightToChangeStatus: boolean;
  userHasRightToRequalifyAsset: boolean;
  userHasRightToOrderQCCheck: boolean;
  userHasRightToStartJob: boolean;
  userHasRightToStartWorkflow: boolean;
  userHasRightToPurgeMediaFiles: boolean;
  userHasRightToDelete: boolean;
}
// #endregion

// #region AssetPanel

export interface AssetPanelDto {
  id: number;
  status: ObjectStatus;
  type: ObjectType;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectedComment?: string;
  bypassedBy?: string;
  bypassedAt?: string;
  bypassedComment?: string;
  isBypassed: boolean;
  deletedBy?: string;
  deletedAt?: string;
  deletedComment?: string;
  userCanPurge: boolean;
  userCanBypassReject: boolean;
  globalInfos: {
    title: string;
    assetName: string;
    classification: ObjectClass;
    requestedBy: string;
    requestedAt: string;
    assignedProviderName: string;
    followedByUserNames: string;
    ingestDate: string;
    uploadedVia: string;
    acceptationDate: string;
    placeholderRowId: number | null;
  };
  assetMetadata: AssetMetadatas;
  assetMedia: AssetMediaCardDto[];
}
// #endregion

// #region shared
export interface AssetMetadataRow {
  tagName: string;
  label: string;
  value: any;
  type: BaseMetadataType;
  id: number;
}

export interface AssetMetadataSection {
  icon: string;
  name: string;
  rows: AssetMetadataRow[];
}

export interface AssetMetadatas {
  // used only in order to be initialized as empty array
  sections: AssetMetadataSection[];
}
// #endregion
